export default `---
slug: ai-knowledge-readiness-framework
title: "AI Knowledge Readiness Framework"
year: 2025
category: work
featured: false
tags: [AI, Knowledge Management, RAG, Framework, Governance, Documentation, GenAI]
thumbnail: /images/projects/ai_knowledge_framework.jpg
description: "A formal framework for making organisational knowledge AI-ready — 3 levels, 3 pillars, 15 guidelines. Published January 2026 and still the company's reference standard."
relatedLinks:
  - label: "Agentforce Chatbot"
    href: /projects/agentforce-chatbot
  - label: "Swift — Enterprise AI"
    href: /career/swift-enterprise-ai
---

![AI Knowledge Readiness Framework](/images/projects/ai_knowledge_framework.jpg)

Sometimes the most useful thing you can ship isn't a product. It's a framework.

In January 2026, I published the **AI Knowledge Readiness Framework** — a formal set of guidelines and best practices for preparing organisational knowledge for AI consumption. It's been the company's reference standard for this topic ever since.

## Where It Came From

The honest origin story is a chatbot that almost worked.

During the [Agentforce customer security chatbot project](#/projects/agentforce-chatbot) in 2025, we ran into a problem that had nothing to do with the model and everything to do with the documents feeding it. PDFs were being parsed in ways that corrupted the chunks retrieved by the RAG pipeline — which meant the AI was generating confident, wrong answers based on malformed inputs. Fixing it required reformatting the source material by hand, document by document.

That experience crystallised something: **organisations don't have an AI problem, they have a knowledge problem.** The models are generally fine. The knowledge bases they're asked to reason over are often not structured in ways that make AI retrieval reliable, consistent, or accurate.

The question was: what would it look like to fix that systematically, rather than reactively?

## The Framework

The AI Knowledge Readiness Framework is structured around **three levels** and **three pillars**.

The three levels reflect where readiness work actually happens:
- **Content level** — the individual documents and knowledge articles themselves
- **Platform level** — the systems that store, index, and serve that content to an AI
- **Organisational level** — the processes, ownership, and governance structures that keep knowledge maintained over time

The three pillars run across all three levels:
- **Quality** — Is the content accurate, complete, and structured in a way that chunks well?
- **Accessibility** — Is it findable? Is metadata in place for retrieval? Is it connected to a RAG pipeline properly?
- **Governance** — Who owns it? How is it maintained? What's the review cycle?

Across the full matrix, the framework defines **15 guidelines** — specific, actionable requirements covering everything from document formatting conventions and metadata schemas to platform configuration for vector search and content ownership models.

## Why It Matters

A RAG system is only as good as what it retrieves. And what it retrieves is only as good as what's in the knowledge base. And what's in the knowledge base is only as good as how it was written, structured, tagged, and maintained.

Most organisations learn this the hard way — after building the AI layer and wondering why it keeps getting things wrong. The framework exists to make that learning happen *before* the system is built, not after.

## The Impact

The framework has been the reference standard for AI knowledge preparation in the company since publication. When new AI projects scope out their knowledge requirements, this is the document they start from.

That's the version of success I'm most proud of: not a flashy launch, but something quietly load-bearing. A piece of infrastructure that makes other things work better.

---

*For the technical background that made this framework possible, see the [Agentforce chatbot project](#/projects/agentforce-chatbot) — where most of these lessons were learned the hard way.*
`;
